/**
 * Navegação por papel (menu inferior) — fonte única do menu do app do tenant.
 *
 * Regras de UX (docs/08): menu inferior fixo com 3–5 itens, nunca mais que 5.
 * A linguagem é por papel (morador vê "fatura/convenção"; o operacional só
 * aparece no Financeiro do síndico e nos Relatórios do contábil).
 *
 * Cada `path` precisa existir na tabela de rotas do web (`apps/web/client/routes.tsx`)
 * e ser permitido ao papel — o teste de rotas do web garante isso. Itens de telas
 * ainda não entregues (ex.: Mural, épico #124) só entram junto com a tela.
 */

/** Papéis de usuário do condomínio + super admin de plataforma. */
export type Role = 'morador' | 'proprietario' | 'sindico' | 'contabil' | 'super_admin';

export const ROLES: readonly Role[] = [
  'morador',
  'proprietario',
  'sindico',
  'contabil',
  'super_admin',
] as const;

/** Item do menu inferior. `path` é a rota client-side da SPA. */
export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly path: string;
  /** Módulo opcional do item (gating, #126); ausente = módulo core, sempre visível. */
  readonly modulo?: string;
}

const NAV: Record<Role, readonly NavItem[]> = {
  morador: [
    { id: 'home', label: 'Início', icon: '🏠', path: '/' },
    { id: 'faturas', label: 'Faturas', icon: '🧾', path: '/faturas' },
    { id: 'convencao', label: 'Convenção', icon: '💬', path: '/convencao', modulo: 'ia_convencao' },
    { id: 'perfil', label: 'Perfil', icon: '👤', path: '/perfil' },
  ],
  proprietario: [
    { id: 'home', label: 'Início', icon: '🏠', path: '/' },
    { id: 'balancetes', label: 'Balancetes', icon: '📊', path: '/balancetes', modulo: 'balancete' },
    { id: 'convencao', label: 'Convenção', icon: '💬', path: '/convencao', modulo: 'ia_convencao' },
    { id: 'perfil', label: 'Perfil', icon: '👤', path: '/perfil' },
  ],
  sindico: [
    { id: 'home', label: 'Início', icon: '🏠', path: '/' },
    { id: 'financeiro', label: 'Financeiro', icon: '💰', path: '/financeiro' },
    { id: 'convencao', label: 'Convenção', icon: '💬', path: '/convencao', modulo: 'ia_convencao' },
    { id: 'ajustes', label: 'Ajustes', icon: '⚙️', path: '/ajustes' },
  ],
  contabil: [
    { id: 'home', label: 'Início', icon: '🏠', path: '/' },
    { id: 'relatorios', label: 'Relatórios', icon: '📊', path: '/relatorios' },
    { id: 'perfil', label: 'Perfil', icon: '👤', path: '/perfil' },
  ],
  super_admin: [
    { id: 'condominios', label: 'Condomínios', icon: '🏢', path: '/' },
    { id: 'engajamento', label: 'Engajamento', icon: '📊', path: '/engajamento' },
    { id: 'ajustes', label: 'Ajustes', icon: '⚙️', path: '/ajustes' },
  ],
};

/** True se `value` é um papel conhecido. */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Itens do menu inferior para o papel informado. Com `modulosAtivos`, esconde os
 * itens de módulo opcional desligado para o tenant (gating em UI, docs/06 §6.5).
 * Retorna `null` quando o papel é desconhecido (o chamador decide o 4xx).
 */
export function navForRole(
  role: string,
  modulosAtivos?: ReadonlySet<string>,
): readonly NavItem[] | null {
  if (!isRole(role)) return null;
  if (!modulosAtivos) return NAV[role];
  return NAV[role].filter((i) => i.modulo === undefined || modulosAtivos.has(i.modulo));
}
